/* ============================================================
   Dathent — Settings screen
   Left tab-rail + main panel per tab. Port of design pack Settings.html.
   Scoped under .settings-page wrapper so pack CSS doesn't leak.
   ============================================================ */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api/client.js';
import { Icons as I } from '../components/pack-icons';
import { BrandIcons } from '../components/pack-icons/brands';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import ThemeToggle from '../components/AppShell/ThemeToggle';
import LangSwitch from '../components/AppShell/LangSwitch';

const TABS = [
  { id: 'profile', group: 'Account', label: 'Profile' },
  { id: 'security', group: 'Account', label: 'Security · 2FA' },
  { id: 'appearance', group: 'Account', label: 'Appearance & language' },
  { id: 'workspace', group: 'Workspace', label: 'Brand & workspace' },
  { id: 'accounts', group: 'Workspace', label: 'Connected social accounts' },
  { id: 'billing', group: 'Billing', label: 'Plan & billing' },
  { id: 'usage', group: 'Billing', label: 'Usage' },
];

const PLAN_DEFS = [
  {
    id: 'pilot', tier: 'Plan 01', name: 'Brand Pilot',
    desc: 'A working operation for one brand. Onboard your business, prepare your first content direction, ship your first calendar.',
    cost: 'From €300 / quarter · Founding Pilot rate',
    cta: 'Switch to Pilot',
    feats: ['One brand onboarded', 'First post preview during onboarding', 'Strategy & first calendar setup', 'Approval & visibility controls', 'Basic platform adaptation'],
  },
  {
    id: 'operator', tier: 'Plan 02', name: 'Marketing Operator',
    desc: 'A complete monthly content operation — strategy, drafts, visuals, calendar, review workflow.',
    cost: 'Talk to us · scoped to campaign',
    cta: 'Current plan',
    feats: ['Full AI business onboarding', 'Monthly content calendar', 'Multi-platform adaptation', 'Visual + carousel', 'Feedback loop & review', 'Priority support'],
  },
  {
    id: 'multi', tier: 'Plan 03', name: 'Multi-Brand',
    desc: 'Operate several brands under one workspace. Per-brand strategy, calendars, and approval flows.',
    cost: 'Talk to us · studios & holdings',
    cta: 'Upgrade',
    feats: ['Multiple brands', 'Per-brand strategy', 'Team approvals + roles', 'Deeper research', 'Cross-brand insight', 'Account manager'],
  },
  {
    id: 'enterprise', tier: 'Plan 04', name: 'Enterprise',
    desc: 'For larger businesses with custom strategy, integrations, and security needs.',
    cost: 'Custom · scoped to business',
    cta: 'Contact sales',
    feats: ['Tailored onboarding', 'SSO & audit', 'Custom strategy & research', 'Security review', 'Success partner'],
  },
];

const CONNECTABLE_PLATFORMS = [
  { id: 'instagram', name: 'Instagram' },
  { id: 'facebook', name: 'Facebook' },
  { id: 'linkedin', name: 'LinkedIn' },
  { id: 'x', name: 'X' },
  { id: 'tiktok', name: 'TikTok' },
  { id: 'threads', name: 'Threads' },
  { id: 'telegram', name: 'Telegram' },
];

function fmtMoney(amount, currency = 'USD') {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function errMessage(err) {
  if (!err) return 'Unknown error';
  return err?.body?.error?.message || err?.body?.message || err?.message || 'Unknown error';
}

/* ---------------- Profile tab ---------------- */
function ProfileTab({ user }) {
  const name = user?.name || user?.fullName || '';
  const email = user?.email || '';

  return (
    <section className="s-card">
      <div className="s-card-head"><h3>Profile</h3></div>
      <div className="s-card-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
          <div className="avatar">{(name || email || 'U').slice(0, 2).toUpperCase()}</div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{name || 'Unnamed account'}</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-subtle)' }}>{email}</div>
          </div>
        </div>
        <div className="form-grid">
          <label className="form-row">
            <span className="form-l">Full name</span>
            <input className="ep-input" value={name} readOnly disabled />
          </label>
          <label className="form-row">
            <span className="form-l">Email</span>
            <input className="ep-input" value={email} readOnly disabled />
          </label>
        </div>
        <p style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-subtle)' }}>
          Profile editing not wired yet — visit the legacy account page if you need to change name or email.
        </p>
      </div>
    </section>
  );
}

/* ---------------- Security tab ---------------- */
function SecurityTab() {
  const [mfa, setMfa] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.auth.getMfaStatus()
      .then((res) => { if (!cancelled) setMfa(res?.data || res); })
      .catch(() => { if (!cancelled) setMfa(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const enabled = Boolean(mfa?.enrolled || mfa?.enabled);
  return (
    <section className="s-card">
      <div className="s-card-head">
        <h3>Two-factor authentication</h3>
        {enabled && <span className="pill success"><span className="dot" />Enabled</span>}
      </div>
      <div className="s-card-body">
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
          Add a second factor to protect your account during sign-in.
        </p>
        {loading ? (
          <div className="muted" style={{ fontSize: 13 }}>Loading…</div>
        ) : enabled ? (
          <p style={{ fontSize: 13 }}>2FA is active for this account.</p>
        ) : (
          <Link to="/2fa/setup" className="btn btn-violet btn-sm">Enable 2FA</Link>
        )}
      </div>
    </section>
  );
}

/* ---------------- Appearance tab ---------------- */
function AppearanceTab() {
  const { i18n } = useTranslation();
  return (
    <>
      <section className="s-card">
        <div className="s-card-head"><h3>Theme</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            Choose how Dathent looks on this device.
          </p>
          <ThemeToggle />
        </div>
      </section>
      <section className="s-card">
        <div className="s-card-head"><h3>Language</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            Current: <strong>{i18n.language}</strong>. UI strings are progressively translated.
          </p>
          <LangSwitch />
        </div>
      </section>
    </>
  );
}

/* ---------------- Workspace tab ---------------- */
function WorkspaceTab() {
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.profile.getBrandProfile()
      .then((res) => { if (!cancelled) setBrand(res?.data?.brandProfile || res?.brandProfile || res?.data || res); })
      .catch(() => { if (!cancelled) setBrand(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);
  return (
    <section className="s-card">
      <div className="s-card-head">
        <h3>Brand & workspace</h3>
        <div className="sp" />
        <Link to="/app/plan" className="btn btn-ghost btn-sm">Edit in Content Plan</Link>
      </div>
      <div className="s-card-body">
        {loading ? <div className="muted">Loading brand profile…</div>
          : !brand ? <p className="muted">No brand profile yet. Set one up via onboarding or Content Plan.</p>
          : (
            <dl className="info-list">
              <div><dt>Brand name</dt><dd>{brand.brandName || brand.name || '—'}</dd></div>
              <div><dt>Tagline</dt><dd>{brand.tagline || '—'}</dd></div>
              <div><dt>Industry</dt><dd>{brand.industry || '—'}</dd></div>
              <div><dt>Audience</dt><dd>{brand.audience || brand.targetAudience || '—'}</dd></div>
              <div><dt>Tone</dt><dd>{Array.isArray(brand.tone) ? brand.tone.join(' · ') : (brand.tone || '—')}</dd></div>
            </dl>
          )}
      </div>
    </section>
  );
}

/* ---------------- Accounts tab ---------------- */
// Messages for OAuth callback results — keys match auth-worker error codes.
const OAUTH_ERROR_MESSAGES = {
  no_facebook_pages: {
    title: 'Facebook connected, but no Pages were found',
    body: 'Meta returned this user has no Facebook Pages. Create a Page (Business or Creator) and reconnect.',
    cta: { label: 'Create Facebook Page', href: 'https://www.facebook.com/pages/create' },
  },
  no_instagram_business_accounts: {
    title: 'Instagram connection needs a Business account',
    body: 'Meta returned no Instagram Business Accounts linked to your Pages. Convert your IG profile to Business (or Creator) and link it to a Facebook Page, then reconnect.',
    cta: { label: 'Convert to Business in Instagram', href: 'https://help.instagram.com/502981923235522' },
  },
  missing_code_or_state: { title: 'OAuth callback missed parameters', body: 'Start the connection again from this page.' },
  invalid_or_expired_state: { title: 'OAuth attempt expired', body: 'The OAuth state expired or no longer matches. Start the connection again.' },
  meta_oauth_failed: { title: 'Meta OAuth failed', body: 'Meta did not complete the OAuth connection. Try again, and verify the Facebook app permissions are configured.' },
  instagram_oauth_failed: { title: 'Instagram OAuth failed', body: 'Instagram did not complete the connection. Try again.' },
  threads_oauth_failed: { title: 'Threads OAuth failed', body: 'Threads did not complete the connection. Try again.' },
  tiktok_oauth_failed: { title: 'TikTok OAuth failed', body: 'TikTok did not complete the connection. Try again.' },
  x_oauth_failed: { title: 'X OAuth failed', body: 'X did not complete the connection. Try again.' },
};

function platformLabelOf(p) {
  const v = String(p || '').toLowerCase();
  if (v === 'x') return 'X';
  if (v === 'tiktok') return 'TikTok';
  if (v === 'linkedin') return 'LinkedIn';
  return v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Account';
}

// Languages this app generates posts in. Stays in sync with auth-worker
// handlers/social-accounts.ts SUPPORTED_PUBLISH_LANGUAGES.
const PUBLISH_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'uk', label: 'Українська' },
  { code: 'ru', label: 'Русский' },
  { code: 'de', label: 'Deutsch' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'pl', label: 'Polski' },
  { code: 'cs', label: 'Čeština' },
  { code: 'pt', label: 'Português' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'lv', label: 'Latviešu' },
];

// Per-account language chip. Two states:
//  - Entitled (Growth+): clickable chip wrapping a hidden native <select>.
//    Shows "Language: English ▾" — explicit so users don't wonder what the
//    chip means. Selecting a value writes through to publish_language.
//  - Locked (free/starter): dashed chip with lock icon, hover tooltip
//    explains gate, click routes to billing.
//
// When the account has no override (value === null), we show the org-wide
// default explicitly: "Language: Default (English)" — so the user sees
// what language posts will ship in, not just that "Default" is set.
function AccountLanguageChip({ value, orgDefault, canOverride, saving, onChange, onUpgrade }) {
  const code = (value || '').toLowerCase() || null;
  const orgCode = (orgDefault || 'en').toLowerCase();
  const orgLabel = PUBLISH_LANGUAGES.find(l => l.code === orgCode)?.label || orgCode.toUpperCase();
  const fullLabel = code
    ? (PUBLISH_LANGUAGES.find(l => l.code === code)?.label || code)
    : `Default (${orgLabel})`;
  if (!canOverride) {
    return (
      <button
        type="button"
        className="account-lang-chip is-locked"
        onClick={onUpgrade}
        title={`Posts to ${orgLabel} (org default). Per-account language is a Growth plan feature.`}
        aria-label={`Language: ${fullLabel}. Upgrade plan to change.`}
      >
        <I.globe size={12} />
        <span className="account-lang-label">Language:</span>
        <span className="account-lang-value">{fullLabel}</span>
        {/* Inline lock SVG — pack-icons doesn't ship one. */}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="11" width="18" height="11" rx="2"/>
          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
      </button>
    );
  }
  return (
    <label
      className="account-lang-chip is-editable"
      style={{ opacity: saving ? 0.6 : 1 }}
      title={code ? `Posts to ${fullLabel}` : `Inherits org default (${orgLabel}). Click to override.`}
    >
      <I.globe size={12} />
      <span className="account-lang-label">Language:</span>
      <span className="account-lang-value">{fullLabel}</span>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ opacity: 0.7 }}>
        <path d="M6 9l6 6 6-6"/>
      </svg>
      <select
        value={code || ''}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={saving}
        style={{
          position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer',
          appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
        }}
        aria-label="Publish language"
      >
        <option value="">Default ({orgLabel})</option>
        {PUBLISH_LANGUAGES.map(l => (
          <option key={l.code} value={l.code}>{l.label}</option>
        ))}
      </select>
    </label>
  );
}

function AccountsTab() {
  const navigate = useNavigate();
  const dash = useDashboardData() || {};
  // Per-account language override = Growth plan or above.
  // Trigger entitlements fetch on mount — DashboardDataProvider only
  // auto-refreshes on the dashboard route, so without this nudge the
  // Settings page sees entitlements=null and falls into the locked
  // branch even for paying customers.
  useEffect(() => {
    if (typeof dash.ensureDashboardData === 'function') {
      dash.ensureDashboardData().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const canOverrideLanguage = Boolean(dash.entitlements?.features?.per_account_language_override);
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [languageSavingId, setLanguageSavingId] = useState(null);
  const [notice, setNotice] = useState(null); // { kind: 'success'|'error', title, body, cta? }

  const handleLanguageChange = async (accountId, language) => {
    if (!canOverrideLanguage) {
      navigate('/app/settings?tab=billing');
      return;
    }
    setLanguageSavingId(accountId);
    setNotice(null);
    // Optimistic update so the chip flips immediately.
    setAccounts(prev => prev.map(a => a.id === accountId ? { ...a, publishLanguage: language || null } : a));
    try {
      await api.socialAccounts.updateLanguage(accountId, language || null);
      // Invalidate the dashboard-level cache so /app/create (and other
      // screens that read socialAccounts from useDashboardData) see the
      // new publishLanguage on the next render — without a full reload.
      // Fire-and-forget; the provider soft-fails on its own.
      dash.invalidateResource?.('socialAccounts');
    } catch (err) {
      const code = err?.body?.error?.code || err?.data?.error?.code;
      if (code === 'ENTITLEMENT_REQUIRED') {
        navigate('/app/settings?tab=billing');
        return;
      }
      setNotice({ kind: 'error', title: 'Could not update language', body: errMessage(err) });
      refresh().catch(() => {});
    } finally {
      setLanguageSavingId(null);
    }
  };

  const refresh = () => {
    setLoading(true);
    return api.socialAccounts.list()
      .then((res) => {
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setAccounts(payload?.accounts || []);
        // Org-wide default falls back to 'en' on the backend when null in D1
        // but here we keep null so the chip can show "Default (org default)"
        // unambiguously even if the user hasn't picked one yet.
        setDefaultLanguage(payload?.defaultPublishLanguage || null);
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  };

  // Parse OAuth result params on mount + on subsequent navigations from
  // /settings/integrations. Then strip the params from the URL so they don't
  // re-fire on refresh.
  useEffect(() => {
    refresh();
    const error = searchParams.get('error');
    const connected = searchParams.get('connected');
    const platform = searchParams.get('platform');
    const accountsCount = searchParams.get('accounts');
    if (error) {
      const cfg = OAUTH_ERROR_MESSAGES[error] || {
        title: `${platformLabelOf(platform)} connection needs attention`,
        body: `Connection finished with code "${error}". Try again or contact support.`,
      };
      setNotice({ kind: 'error', ...cfg });
    } else if (connected) {
      setNotice({
        kind: 'success',
        title: `${platformLabelOf(platform)} connected`,
        body: accountsCount ? `${accountsCount} account${accountsCount === '1' ? '' : 's'} now available for publishing.` : 'Account is ready for publishing.',
      });
    }
    if (error || connected) {
      const next = new URLSearchParams(searchParams);
      ['error', 'connected', 'platform', 'accounts', 'pages', 'instagramBusinessAccounts', 'provider', 'assistant', 'continue'].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectedPlatforms = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()));

  const handleConnect = async (platform) => {
    setConnecting(platform);
    setNotice(null);
    try {
      const result = await api.socialAccounts.connect(platform, {
        returnPath: '/app/settings?tab=accounts',
      });
      // Telegram returns a link payload (not redirect); show to user
      if (result?.link?.url || result?.deepLink) {
        const url = result.link?.url || result.deepLink;
        window.open(url, '_blank', 'noopener');
      }
      // Redirect happens via window.location.assign for OAuth platforms
    } catch (err) {
      setNotice({ kind: 'error', title: 'Connection failed to start', body: errMessage(err) });
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (id) => {
    const account = accounts.find((a) => a.id === id);
    const label = account?.displayName || account?.externalAccountId || platformLabelOf(account?.platform);
    if (!window.confirm(`Disconnect ${label}? You can reconnect any time.`)) return;
    setNotice(null);
    try {
      await api.socialAccounts.disconnect(id);
      await refresh();
      setNotice({ kind: 'success', title: 'Account disconnected', body: `${label} is no longer connected. Posts already scheduled to it stay scheduled until you remove them.` });
    } catch (err) {
      setNotice({ kind: 'error', title: 'Disconnect failed', body: errMessage(err) });
    }
  };

  const handleReconnect = async (account) => {
    const platform = String(account.platform || '').toLowerCase();
    handleConnect(platform);
  };

  return (
    <>
      {notice && (
        <div
          className={`s-notice s-notice-${notice.kind}`}
          role={notice.kind === 'error' ? 'alert' : 'status'}
          style={{
            padding: '12px 14px',
            marginBottom: 16,
            borderRadius: 'var(--r-md)',
            border: `1px solid ${notice.kind === 'error' ? 'var(--danger)' : 'var(--success)'}`,
            background: notice.kind === 'error' ? 'var(--danger-soft)' : 'var(--success-soft)',
            color: notice.kind === 'error' ? 'var(--danger-text, var(--danger))' : 'var(--success-text, var(--success))',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
          }}
        >
          {notice.kind === 'error'
            ? <I.alert size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            : <I.check size={16} style={{ flexShrink: 0, marginTop: 2 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{notice.title}</div>
            {notice.body && <div style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>{notice.body}</div>}
            {notice.cta?.href && (
              <a
                className="btn btn-soft btn-sm"
                href={notice.cta.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginTop: 10, display: 'inline-flex' }}
              >
                {notice.cta.label} <I.external size={11} style={{ marginLeft: 6 }} />
              </a>
            )}
          </div>
          <button
            className="btn btn-ghost btn-sm btn-icon"
            type="button"
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            style={{ flexShrink: 0 }}
          >
            <I.x size={13} />
          </button>
        </div>
      )}
      <section className="s-card">
        <div className="s-card-head">
          <h3>Connected social accounts</h3>
        </div>
        <div className="s-card-body">
          {loading ? (
            <div className="muted">Loading accounts…</div>
          ) : accounts.length === 0 ? (
            <p className="muted">No accounts connected yet. Pick a platform below.</p>
          ) : (
            <ul className="account-list">
              {accounts.map((a) => {
                const platform = (a.platform || '').toLowerCase();
                const needsReconnect = a.status === 'needs_reconnect'
                  || a.status === 'expired'
                  || a.status === 'failed';
                return (
                  <li key={a.id} className="account-row">
                    <span
                      className={`ob-net-ic-${platform}`}
                      style={{ width: 32, height: 32, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                    >
                      {BrandIcons[platform]?.({ size: 16 })}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="account-name">{a.displayName || a.externalAccountId || platform}</div>
                      <div className="account-sub">
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        {a.status && a.status !== 'connected' && ` · ${a.status}`}
                      </div>
                    </div>
                    {needsReconnect ? (
                      <button
                        className="btn btn-soft btn-sm"
                        onClick={() => handleReconnect(a)}
                        disabled={connecting === platform}
                        type="button"
                      >
                        Reconnect
                      </button>
                    ) : (
                      <span className="pill success"><span className="dot" />Connected</span>
                    )}
                    {/* Per-account publish language. Growth+ → native dropdown.
                        Free/starter → locked chip routes to billing. When
                        publishLanguage is null we show the org default in
                        parens so the user sees what'll actually ship. */}
                    <AccountLanguageChip
                      value={a.publishLanguage}
                      orgDefault={defaultLanguage}
                      canOverride={canOverrideLanguage}
                      saving={languageSavingId === a.id}
                      onChange={(lang) => handleLanguageChange(a.id, lang)}
                      onUpgrade={() => navigate('/app/settings?tab=billing')}
                    />
                    {a.platform === 'instagram' && (
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => navigate(`/app/messages?accountId=${encodeURIComponent(a.id)}`)}
                        title="View Instagram Direct Messages for this account"
                      >
                        Messages
                      </button>
                    )}
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleDisconnect(a.id)}
                      type="button"
                    >
                      Disconnect
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      <section className="s-card">
        <div className="s-card-head"><h3>Connect a new account</h3></div>
        <div className="s-card-body">
          <p className="muted" style={{ fontSize: 13, marginTop: 0, marginBottom: 12 }}>
            Click a platform to start the OAuth flow. You'll be redirected back here after authorising.
          </p>
          <div className="connect-grid">
            {CONNECTABLE_PLATFORMS.map((p) => (
              <button
                key={p.id}
                className="connect-btn"
                onClick={() => handleConnect(p.id)}
                disabled={connecting === p.id}
                type="button"
              >
                <span
                  className={`ob-net-ic-${p.id}`}
                  style={{ width: 26, height: 26, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}
                >
                  {BrandIcons[p.id]?.({ size: 13 })}
                </span>
                <span style={{ flex: 1 }}>{p.name}</span>
                {connectedPlatforms.has(p.id) && (
                  <span style={{ fontSize: 11, color: 'var(--success-text)' }}>·  linked</span>
                )}
                {connecting === p.id ? '…' : <I.arrowRight size={12} />}
              </button>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ---------------- Billing tab ---------------- */
function BillingTab({ entitlements }) {
  const planName = entitlements?.planName || entitlements?.plan || 'Free';
  const planDescription = entitlements?.planDescription
    || 'A complete monthly content operation — onboarding, strategy, drafts, visuals, and a calendar your team approves and ships.';
  const nextInvoiceAt = entitlements?.nextInvoiceAt || entitlements?.currentPeriodEnd;
  const customerPortalUrl = entitlements?.customerPortalUrl;
  const usage = entitlements?.usage || {};

  const [invoices, setInvoices] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Best-effort fetch — endpoints may not exist yet
    Promise.allSettled([
      api.billing.getInvoices?.() || Promise.reject(new Error('not-wired')),
      api.billing.getPaymentMethod?.() || Promise.reject(new Error('not-wired')),
    ]).then(([invRes, pmRes]) => {
      if (cancelled) return;
      if (invRes.status === 'fulfilled') {
        setInvoices(invRes.value?.invoices || invRes.value?.data?.invoices || []);
      }
      if (pmRes.status === 'fulfilled') {
        setPaymentMethod(pmRes.value?.paymentMethod || pmRes.value?.data?.paymentMethod || null);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const [portalLoading, setPortalLoading] = useState(false);
  const handleManageBilling = async () => {
    setPortalLoading(true);
    try {
      // If entitlements already include a customer portal URL, prefer that
      if (customerPortalUrl) {
        window.open(customerPortalUrl, '_blank', 'noopener');
        return;
      }
      // Otherwise create a Stripe portal session on demand
      const res = await api.billing.createPortalSession({ returnUrl: window.location.href });
      const url = res?.url || res?.data?.url || res?.portalUrl;
      if (url) {
        window.location.assign(url);
      } else {
        window.alert('Billing portal URL not returned by server.');
      }
    } catch (err) {
      window.alert(`Could not open billing portal: ${errMessage(err)}`);
    } finally {
      setPortalLoading(false);
    }
  };

  const currentPlanId = entitlements?.planId
    || (planName.toLowerCase().includes('operator') ? 'operator' : null);

  return (
    <>
      <div className="head">
        <h2>Plan & billing</h2>
        <p className="lede">
          Plans scale with brand depth, strategy support, and operational coverage. Post count isn't the value here — the marketing operation is.
        </p>
      </div>

      {/* Current plan */}
      <section className="current-plan">
        <div>
          <span className="tier">Current plan</span>
          <div className="name">{planName}</div>
          <p className="desc">{planDescription}</p>
          <div className="meta">
            {nextInvoiceAt && (
              <span><b>Next invoice</b> · {new Date(nextInvoiceAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
            )}
            {entitlements?.billingInterval && (
              <span><b>Billing period</b> · {entitlements.billingInterval}</span>
            )}
            <span><b>Brands</b> · 1 of included scope</span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-violet btn-lg" onClick={handleManageBilling} type="button">
            Manage billing
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handleManageBilling} type="button">
            Change plan
          </button>
        </div>
      </section>

      {/* This month's operation */}
      <section className="s-card">
        <div className="s-card-head">
          <h3>This month's operation</h3>
          {planName !== 'Free' && <span className="pill accent"><span className="dot" />Operator-level scope</span>}
        </div>
        <div className="s-card-body">
          <div className="usage-grid">
            {Object.entries(usage).slice(0, 6).map(([key, data]) => {
              const used = data?.used ?? 0;
              const limit = data?.limit;
              const limitDisplay = limit == null ? '∞' : limit;
              const pct = limit && limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
              return (
                <div key={key} className="usage-cell">
                  <div className="lbl">{key.replace(/_/g, ' ')}</div>
                  <div className="v">{used} / {limitDisplay}</div>
                  {limit && limit > 0 && (
                    <div className="pr"><div className="pr-fill" style={{ width: pct + '%' }} /></div>
                  )}
                  {data?.remaining != null && (
                    <div className="sub">{data.remaining === null ? 'Unlimited' : `${data.remaining} remaining`}</div>
                  )}
                </div>
              );
            })}
            {Object.keys(usage).length === 0 && (
              <>
                <div className="usage-cell"><div className="lbl">Strategy refresh</div><div className="v">— / —</div><div className="sub">Awaiting entitlements</div></div>
                <div className="usage-cell"><div className="lbl">Content batches</div><div className="v">— / —</div><div className="sub">Awaiting entitlements</div></div>
                <div className="usage-cell"><div className="lbl">Visual concepts</div><div className="v">— / —</div><div className="sub">Awaiting entitlements</div></div>
              </>
            )}
          </div>
          <div className="banner" style={{ marginTop: 18 }}>
            <I.info size={16} />
            Operator scope is the value here — not a post count cap. Heavy months are normal; we'll tell you if a campaign needs more capacity.
          </div>
        </div>
      </section>

      {/* Plans */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <h3 style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.015em', margin: 0 }}>Change plan</h3>
          <span style={{ color: 'var(--text-subtle)', fontSize: 13 }}>Plans scale with brand depth, not post counts.</span>
        </div>
        <div className="plan-grid">
          {PLAN_DEFS.map((p) => {
            const isCurrent = currentPlanId === p.id || planName === p.name;
            return (
              <article key={p.id} className={`plan-card ${isCurrent ? 'is-current' : ''}`}>
                {isCurrent && <div className="now-tag">Current</div>}
                <div className="tier">{p.tier}</div>
                <div className="name">{p.name}</div>
                <p className="desc">{p.desc}</p>
                <div className="cost">{p.cost.split('·').map((part, i) => i === 0 ? <b key={i}>{part.trim()}</b> : <span key={i}> · {part.trim()}</span>)}</div>
                <div className="cta">
                  {isCurrent ? (
                    <button className="btn btn-violet btn-lg" disabled style={{ width: '100%', opacity: 0.7 }}>
                      Current plan
                    </button>
                  ) : (
                    <button className="btn btn-secondary btn-lg" style={{ width: '100%' }} onClick={handleManageBilling} type="button">
                      {p.cta}
                    </button>
                  )}
                </div>
                <ul className="feats">
                  {p.feats.map((f, i) => (
                    <li key={i}>
                      <span className="ck"><I.check size={10} /></span>{f}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      {/* Payment method */}
      <section className="s-card">
        <div className="s-card-head">
          <h3>Payment method</h3>
          {paymentMethod && <span className="pill success"><span className="dot" />Verified</span>}
          <div className="sp" />
          <button className="btn btn-secondary btn-sm" onClick={handleManageBilling} type="button">Update</button>
        </div>
        <div className="s-card-body">
          {paymentMethod ? (
            <div className="pm-card">
              <div className="pm-brand">{(paymentMethod.brand || 'CARD').toUpperCase()}</div>
              <div>
                <div className="pm-name">{paymentMethod.brand} · ending {paymentMethod.last4}</div>
                <div className="pm-meta">Expires {paymentMethod.expMonth}/{paymentMethod.expYear}</div>
              </div>
            </div>
          ) : (
            <p className="muted">No payment method on file. Add one via Manage billing.</p>
          )}
        </div>
      </section>

      {/* Invoices */}
      <section className="s-card">
        <div className="s-card-head">
          <h3>Invoices</h3>
          <div className="sp" />
          <button className="btn btn-ghost btn-sm" onClick={handleManageBilling} type="button">View all</button>
        </div>
        <div>
          {invoices.length === 0 ? (
            <div className="s-empty">No invoices yet.</div>
          ) : (
            invoices.slice(0, 5).map((inv) => (
              <div key={inv.id || inv.number} className="invoice-row">
                <div className="inv-no">{inv.number || inv.id}</div>
                <div className="inv-mid">
                  <div className="inv-title">{inv.description || `${planName}`}</div>
                  <div className="inv-sub">
                    {inv.status === 'paid' ? 'Paid' : inv.status} ·
                    {inv.createdAt ? ` ${new Date(inv.createdAt).toLocaleDateString('en', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  </div>
                </div>
                <span className="pill success"><span className="dot" />{inv.status === 'paid' ? 'Paid' : inv.status}</span>
                {inv.pdfUrl && (
                  <a className="btn btn-ghost btn-sm" href={inv.pdfUrl} target="_blank" rel="noopener">
                    <I.download size={12} /> PDF
                  </a>
                )}
              </div>
            ))
          )}
        </div>
      </section>
    </>
  );
}

/* ---------------- Usage tab ---------------- */
function UsageTab({ entitlements, counts }) {
  const usage = entitlements?.usage || {};
  return (
    <section className="s-card">
      <div className="s-card-head"><h3>Usage detail</h3></div>
      <div className="s-card-body">
        <dl className="info-list">
          <div><dt>Total content created</dt><dd>{counts?.all || 0}</dd></div>
          <div><dt>Published posts</dt><dd>{counts?.published || 0}</dd></div>
          <div><dt>Drafts</dt><dd>{counts?.draft || 0}</dd></div>
          {Object.entries(usage).map(([key, data]) => (
            <div key={key}>
              <dt>{key.replace(/_/g, ' ')}</dt>
              <dd>{data?.used ?? 0}{data?.limit != null ? ` / ${data.limit}` : ''}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/* ---------------- Main screen ---------------- */
// Legacy tab names from the old app and OAuth-return redirects → new pack ids.
const TAB_ALIASES = {
  social: 'accounts',
  integrations: 'accounts',
  brand: 'workspace',
  general: 'profile',
};

export const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  // Resolve the requested tab (aliases + unknown fallback) into a known id.
  const resolved = (() => {
    const raw = urlTab || 'billing';
    const aliased = TAB_ALIASES[raw] || raw;
    return TABS.find((t) => t.id === aliased) ? aliased : 'billing';
  })();
  const [active, setActive] = useState(resolved);

  // Follow the URL: when the user navigates with ?tab=… from elsewhere
  // (e.g. sidebar account menu) while Settings is already mounted, keep
  // `active` in sync. Also normalize alias / invalid values in the URL
  // so deep-links stay stable.
  useEffect(() => {
    if (resolved !== active) setActive(resolved);
    if (urlTab && urlTab !== resolved) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', resolved);
      setSearchParams(next, { replace: true });
    }
  }, [urlTab, resolved]);

  const { user } = useAuth();
  const { entitlements } = useDashboardData() || {};
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.content.feed({ limit: 1 })
      .then((res) => { if (!cancelled) setCounts(res?.counts || res?.data?.counts || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const tab of TABS) {
      if (!groups.has(tab.group)) groups.set(tab.group, []);
      groups.get(tab.group).push(tab);
    }
    return groups;
  }, []);

  const switchTab = (id) => {
    setActive(id);
    const next = new URLSearchParams(searchParams);
    next.set('tab', id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="settings-page">
      <aside className="sb">
        <h1>Settings</h1>
        {Array.from(grouped.entries()).map(([group, tabs]) => (
          <React.Fragment key={group}>
            <div className="sb-group">{group}</div>
            {tabs.map((t) => (
              <button
                key={t.id}
                className={`sb-item ${active === t.id ? 'is-active' : ''}`}
                onClick={() => switchTab(t.id)}
                type="button"
              >
                {t.label}
              </button>
            ))}
          </React.Fragment>
        ))}
      </aside>

      <main className="settings-main">
        {active === 'profile' && <ProfileTab user={user} />}
        {active === 'security' && <SecurityTab />}
        {active === 'appearance' && <AppearanceTab />}
        {active === 'workspace' && <WorkspaceTab />}
        {active === 'accounts' && <AccountsTab />}
        {active === 'billing' && <BillingTab entitlements={entitlements} />}
        {active === 'usage' && <UsageTab entitlements={entitlements} counts={counts} />}
      </main>
    </div>
  );
};

export default Settings;
